// Real market lookups: Kalshi through the app's signed client, Polymarket straight from its public gateway.

import type { Leg, Venue } from './data';

/** The app's Kalshi `getMarket`; Kalshi needs the extension to sign, so the preview borrows the client. */
export type KalshiGetMarket = (ticker: string) => Promise<any>;

const PM_GATEWAY = 'https://gateway.polymarket.us';
/** Polymarket's endDate is a backstop until a market closes; kickoff plus this is the realistic end. */
const PM_GAME_MS = 3 * 3_600_000;
const KALSHI_DONE = ['settled', 'finalized', 'determined'];
/** The app's client waits on the extension; without it a lookup would hang rather than fail. */
const KALSHI_TIMEOUT_MS = 6_000;

/** Accepts a bare ticker/slug or a venue URL, and normalises case. */
export function cleanTicker(venue: Venue, raw: string): string {
  let s = raw.trim();
  if (/^https?:\/\//i.test(s)) s = s.replace(/[?#].*$/, '').replace(/\/+$/, '').split('/').pop() ?? '';
  return venue === 'kalshi' ? s.toUpperCase() : s.toLowerCase();
}

/** Short readable name from a Polymarket question; `title` is unreliable (sometimes just a player name). */
export function pmName(question: string, title: string): string {
  const q = question.trim().replace(/\?$/, '');
  let m = /^Who will win in the upcoming \w+ event (.+?) scheduled/.exec(q);
  if (m) return m[1];
  m = /^Will the total in (.+?) be more than ([\d.]+)/.exec(q);
  if (m) return `${m[1]}: over ${m[2]} points`;
  m = /^Will the (.+?) cover (\S+) vs the (.+?) in /.exec(q);
  if (m) return `${m[1]} ${m[2]} vs ${m[3]}`;
  m = /^Will (.+)$/.exec(q);
  if (m) return m[1][0].toUpperCase() + m[1].slice(1);
  return q || title;
}

function time(v: unknown): number | null {
  const t = Date.parse(String(v ?? ''));
  return Number.isFinite(t) ? t : null;
}

export async function lookupKalshi(get: KalshiGetMarket, ticker: string): Promise<Leg> {
  let timer = 0;
  const m = await Promise.race([
    get(ticker),
    new Promise<never>((_, reject) => { timer = window.setTimeout(() => reject(new Error('Kalshi did not answer')), KALSHI_TIMEOUT_MS); }),
  ]).finally(() => window.clearTimeout(timer));
  if (!m?.ticker) throw new Error('not found on Kalshi');
  return {
    venue: 'kalshi', ticker: m.ticker,
    title: String(m.title || m.yes_sub_title || m.ticker).replace(/\?$/, ''),
    expiresAt: time(m.expected_expiration_time) ?? time(m.close_time),
    status: KALSHI_DONE.includes(m.status) || m.result === 'yes' || m.result === 'no' ? 'settled' : 'open',
  };
}

export async function lookupPolymarket(slug: string): Promise<Leg> {
  const r = await fetch(`${PM_GATEWAY}/v1/market/slug/${encodeURIComponent(slug)}`);
  if (!r.ok) throw new Error(r.status === 404 || r.status === 500 ? 'not a Polymarket market slug' : `Polymarket HTTP ${r.status}`);
  const m = (await r.json())?.market;
  if (!m?.slug) throw new Error('not a Polymarket market slug');
  const closed = Boolean(m.closed) || m.status === 'MARKET_STATUS_RESOLVED';
  const start = time(m.gameStartTime);
  return {
    venue: 'polymarket', ticker: m.slug,
    title: pmName(String(m.question ?? ''), String(m.title ?? '')),
    expiresAt: closed || start === null ? time(m.endDate) : start + PM_GAME_MS,
    status: closed ? 'settled' : 'open',
  };
}

export function lookup(venue: Venue, ticker: string, kalshi: KalshiGetMarket): Promise<Leg> {
  return venue === 'kalshi' ? lookupKalshi(kalshi, ticker) : lookupPolymarket(ticker);
}
